"use client";

import * as React from "react";
import * as RechartsPrimitive from "recharts";

import { cn } from "@hipotecar/ui/lib/utils";

// Format: { [key: string]: { label: string, color: string } }
export type ChartConfig = {
	[k in string]: {
		label?: React.ReactNode;
		icon?: React.ComponentType;
	} & (
		| { color?: string; theme?: never }
		| { color?: never; theme: Record<keyof ChartTheme, string> }
	);
};

type ChartTheme = {
	light: string;
	dark: string;
};

interface ChartContextProps {
	config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
	const context = React.useContext(ChartContext);

	if (!context) {
		throw new Error("useChart must be used within a ChartContainer");
	}

	return context;
}

const ChartContainer = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div"> & {
		config: ChartConfig;
		/** Matches ResponsiveContainer: a pixel number or a percentage string. */
		height?: number | `${number}%`;
		children: React.ComponentProps<
			typeof RechartsPrimitive.ResponsiveContainer
		>["children"];
	}
>(({ className, children, config, height, ...props }, ref) => {
	const uniqueId = React.useId();
	const chartId = `chart-${uniqueId.replace(/:/g, "")}`;

	return (
		<ChartContext.Provider value={{ config }}>
			<div
				style={
					{
						"--chart-1": "var(--color-brand-500)",
						"--chart-2": "var(--color-brand-300)",
						"--chart-3": "var(--color-blue-light-500)",
						"--chart-4": "var(--color-success-500)",
						"--chart-5": "var(--color-warning-500)",
					} as React.CSSProperties
				}
				ref={ref}
				className={cn(
					"[&_.recharts-dot]:stroke-width-2 [&_.recharts-polar-grid-concentric-circle]:stroke-width-1 [&_.recharts-polar-grid-concentric-polygon]:stroke-width-1 flex aspect-video justify-center text-xs [&_.recharts-cartesian-grid-horizontal_line]:stroke-border [&_.recharts-cartesian-grid-vertical_line]:stroke-border [&_.recharts-dot]:fill-background [&_.recharts-grid-line]:stroke-border [&_.recharts-layer]:outline-none [&_.recharts-polar-grid-concentric-circle]:fill-none [&_.recharts-polar-grid-concentric-circle]:stroke-border [&_.recharts-polar-grid-concentric-polygon]:fill-none [&_.recharts-polar-grid-concentric-polygon]:stroke-border [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-tooltip-cursor]:stroke-border",
					className,
				)}
				data-chart={chartId}
				{...props}
			>
				<ChartStyle id={chartId} config={config} />
				<RechartsPrimitive.ResponsiveContainer
					debounce={100}
					minWidth={0}
					height={height ?? "100%"}
				>
					{children}
				</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
	const colorConfig = Object.entries(config).filter(
		([, config]) => config.theme || config.color,
	);

	if (!colorConfig.length) {
		return null;
	}

	return (
		<style
			dangerouslySetInnerHTML={{
				__html: Object.entries(config)
					.map(([key, config]) => {
						const color =
							config.theme?.[isDarkMode() ? "dark" : "light"] || config.color;
						return color
							? `[data-chart=${id}] { --color-${key}: ${color}; }`
							: null;
					})
					.join("\n"),
			}}
		/>
	);
};

const isDarkMode = () => {
	if (typeof window === "undefined") return false;
	return document.documentElement.classList.contains("dark");
};

const ChartTooltip = RechartsPrimitive.Tooltip;

interface ChartPayloadItem {
	dataKey?: string | number;
	name?: string;
	value?: number | string | (number | string)[];
	payload?: Record<string, unknown>;
	color?: string;
	fill?: string;
	stroke?: string;
}

const ChartTooltipContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div"> & {
		active?: boolean;
		payload?: ChartPayloadItem[];
		label?: string | number;
		labelFormatter?: (
			label: React.ReactNode,
			payload: ChartPayloadItem[],
		) => React.ReactNode;
		labelClassName?: string;
		formatter?: (
			value: number | string | (number | string)[],
			name: string,
			item: ChartPayloadItem,
			index: number,
			payload: ChartPayloadItem[],
		) => React.ReactNode;
		hideLabel?: boolean;
		hideIndicator?: boolean;
		indicator?: "line" | "dot" | "dashed";
		nameKey?: string;
		labelKey?: string;
		payloadOrder?: string[];
	}
>(
	(
		{
			active,
			payload,
			className,
			indicator = "dot",
			hideLabel = false,
			hideIndicator = false,
			label,
			labelFormatter,
			labelClassName,
			formatter,
			color,
			nameKey,
			labelKey,
			payloadOrder,
		},
		ref,
	) => {
		const { config } = useChart();

		if (formatter) {
			// use formatter if needed
		}

		const tooltipLabel = React.useMemo(() => {
			if (hideLabel || !payload?.length) {
				return null;
			}

			const [item] = payload;
			const key = `${labelKey || item.dataKey || item.name || "value"}`;
			const itemConfig = getPayloadConfigFromPayload(config, item, key);
			const value =
				!labelKey && typeof label === "string"
					? config[label as keyof typeof config]?.label || label
					: itemConfig?.label;

			if (labelFormatter) {
				return (
					<div className={cn("font-medium text-foreground", labelClassName)}>
						{labelFormatter(value, payload)}
					</div>
				);
			}

			if (!value) {
				return null;
			}

			return (
				<div
					className={cn(
						"font-medium text-gray-900 dark:text-white",
						labelClassName,
					)}
				>
					{value}
				</div>
			);
		}, [
			label,
			labelFormatter,
			payload,
			hideLabel,
			labelClassName,
			config,
			labelKey,
		]);

		const sortedPayload = React.useMemo(() => {
			if (!payloadOrder || !payload) return payload;
			return [...payload].sort((a, b) => {
				const aKey = `${a.dataKey || a.name || ""}`;
				const bKey = `${b.dataKey || b.name || ""}`;
				return payloadOrder.indexOf(aKey) - payloadOrder.indexOf(bKey);
			});
		}, [payload, payloadOrder]);

		if (!active || !payload?.length) {
			return null;
		}

		const nestLabel = payload.length === 1 && indicator !== "dot";

		return (
			<div
				ref={ref}
				className={cn(
					"grid min-w-32 items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
					className,
				)}
			>
				{!nestLabel ? tooltipLabel : null}
				<div className="grid gap-1.5">
					{sortedPayload?.map((item: ChartPayloadItem, index) => {
						const key = `${nameKey || item.dataKey || item.name || "value"}`;
						const itemConfig = getPayloadConfigFromPayload(config, item, key);
						const indicatorColor =
							color || itemConfig?.color || item.payload?.fill || item.color;

						return (
							<div
								key={item.dataKey}
								className={cn(
									"flex w-full items-stretch gap-2 [&>svg]:size-2.5 [&>svg]:text-muted-foreground",
									indicator === "dot" && "items-center",
								)}
							>
								{itemConfig?.icon ? (
									<itemConfig.icon />
								) : (
									!hideIndicator && (
										<div
											className={cn(
												"shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
												{
													"h-2.5 w-2.5": indicator === "dot",
													"w-1": indicator === "line",
													"w-0 border-l-2 border-dashed bg-transparent":
														indicator === "dashed",
													"my-0.5": nestLabel && indicator === "dashed",
												},
											)}
											style={
												{
													"--color-bg": indicatorColor,
													"--color-border": indicatorColor,
												} as React.CSSProperties
											}
										/>
									)
								)}
								<div
									className={cn(
										"flex flex-1 justify-between gap-1.5 leading-none",
										nestLabel ? "items-end" : "items-center",
									)}
								>
									<div className="grid gap-1.5">
										{nestLabel ? tooltipLabel : null}
										<span className="text-gray-600 dark:text-gray-300">
											{itemConfig?.label || item.name}
										</span>
									</div>
									{typeof item.value !== "undefined" && item.value !== null && (
										<span className="font-mono font-medium whitespace-nowrap text-gray-900 tabular-nums dark:text-white">
											{formatter
												? formatter(
														item.value!,
														item.name as string,
														item,
														index,
														payload || [],
													)
												: item.value?.toLocaleString()}
										</span>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div"> & {
		payload?: ChartPayloadItem[];
		verticalAlign?: "top" | "bottom" | "middle";
		hideIcon?: boolean;
		nameKey?: string;
		payloadOrder?: string[];
	}
>(
	(
		{
			className,
			hideIcon = false,
			payload,
			verticalAlign,
			nameKey,
			payloadOrder,
		},
		ref,
	) => {
		const { config } = useChart();
		const sortedPayload = React.useMemo(() => {
			if (!payloadOrder || !payload) return payload;
			return [...payload].sort((a, b) => {
				const aKey = `${a.dataKey || a.name || ""}`;
				const bKey = `${b.dataKey || b.name || ""}`;
				return payloadOrder.indexOf(aKey) - payloadOrder.indexOf(bKey);
			});
		}, [payload, payloadOrder]);

		if (!payload?.length) {
			return null;
		}

		return (
			<div
				ref={ref}
				className={cn(
					"flex items-center justify-center gap-4",
					verticalAlign === "top" ? "pb-3" : "pt-3",
					className,
				)}
			>
				{sortedPayload?.map((item: ChartPayloadItem) => {
					const key = `${nameKey || item.dataKey || item.name || "value"}`;
					const itemConfig = getPayloadConfigFromPayload(config, item, key);

					return (
						<div
							key={`${item.dataKey}-${item.name}-${item.value}`}
							className={cn(
								"flex items-center gap-1.5 text-foreground [&>svg]:size-3",
							)}
						>
							{itemConfig?.icon && !hideIcon ? (
								<itemConfig.icon />
							) : (
								<div
									className="size-2 shrink-0 rounded-[2px]"
									style={{
										backgroundColor: itemConfig?.color || item.color,
									}}
								/>
							)}
							{itemConfig?.label}
						</div>
					);
				})}
			</div>
		);
	},
);
ChartLegendContent.displayName = "ChartLegend";

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
	config: ChartConfig,
	payload: unknown,
	key: string,
) {
	if (typeof payload !== "object" || payload === null) {
		return undefined;
	}

	const payloadPayload =
		"payload" in payload &&
		typeof payload.payload === "object" &&
		payload.payload !== null
			? payload.payload
			: undefined;

	let configLabelKey: string = key;

	if (
		key in payload &&
		typeof payload[key as keyof typeof payload] === "string"
	) {
		configLabelKey = payload[key as keyof typeof payload] as string;
	} else if (
		payloadPayload &&
		key in payloadPayload &&
		typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
	) {
		configLabelKey = payloadPayload[
			key as keyof typeof payloadPayload
		] as string;
	}

	return configLabelKey in config
		? config[configLabelKey]
		: config[key as keyof typeof config];
}

export {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	ChartLegend,
	ChartLegendContent,
	ChartStyle,
};
